/**
 * The "Your data" section of `/settings` (screens.md §/settings, design.md §5.5):
 * async native-JSON export (emailed link + in-app download) and import
 * (choose a source → upload → poll → results, `duplicate_of` short-circuit).
 * Whole card is gated on the `import_export_v1` mobile-config flag, read via
 * `GET /config/mobile`; a missing/disabled flag hides the card, a failing
 * config endpoint shows a quiet "not available" line (never a crash).
 *
 * The import half is now a stepper (`ImportWizard`): the user tells us where
 * their data comes from (TV Time / Trakt / Letterboxd / Simkl / ScreenAtlas /
 * other CSV, or "I'm not sure" → `auto`), uploads whatever file they have,
 * watches it detect + import, then sees the detected format, counts, and the
 * grouped unmatched report. The backend sniffs the bytes — the source choice
 * is only a hint (`POST /v1/account/import/{id}/start` `{ source }`).
 *
 * Mobile-only (`md:hidden`): at `md`+ the desktop rail's "Import / export"
 * item opens `ImportExportWizard` instead, which reuses `ExportSection` and
 * `ImportWizard` from this file rather than duplicating their logic.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Download, LibraryBig, Loader2, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@/api/http';
import { NAV_ITEMS } from '@/components/nav-items';
import { Button } from '@/components/ui/button';
import { useLocaleOptional, type Locale } from '@/i18n/locale';

/** Map a thrown error to a `profile.yourData.*` message key. */
function errKey(e: unknown, kind: 'export' | 'import'): string {
  if (e instanceof ApiError && e.status === 429) return `profile.yourData.${kind}RateLimited`;
  if (e instanceof ApiError && e.status === 409) return `profile.yourData.${kind}InProgress`;
  return `profile.yourData.${kind}Failed`;
}

import {
  cancelImport,
  createExport,
  importUpload,
  MY_EXPORTS_QUERY_KEY,
  MY_IMPORTS_QUERY_KEY,
  startImport,
  useImportExportGate,
  useImportReport,
  useMyExports,
  useMyImports,
  usePollExport,
  usePollImport,
  type ExportJob,
  type ImportJob,
  type ImportSource,
} from './api';
import { relativeTime } from './format';
import { SettingsSection } from './SettingRow';

export function DataExportImport({ order }: { order?: number } = {}) {
  const gate = useImportExportGate();
  const { t } = useLocaleOptional();

  if (gate.isPending || gate.data === 'off') return null;

  if (gate.data === 'unavailable') {
    return (
      <SettingsSection
        id="data"
        order={order}
        className="md:hidden"
        title={t('profile.yourData')}
        description={t('profile.yourData.notAvailable')}
      >
        {null}
      </SettingsSection>
    );
  }

  return (
    <SettingsSection id="data" order={order} className="md:hidden" title={t('profile.yourData')} description={t('profile.yourDataHint')}>
      <div className="flex flex-col gap-5 py-3">
        <ExportSection />
        <ImportWizard />
      </div>
    </SettingsSection>
  );
}

/* --- export --------------------------------------------------------------- */

/** A completed export is "recent" while its `created_at` is under 24 h old. */
const RECENT_EXPORT_WINDOW_MS = 24 * 60 * 60 * 1000;

/** The newest completed, downloadable export created in the last 24 h. */
function findRecentExport(jobs: ExportJob[] | undefined, now: number = Date.now()): ExportJob | undefined {
  if (!jobs) return undefined;
  return jobs.find((job) => {
    if (job.status !== 'completed' || !job.downloadUrl || !job.createdAt) return false;
    const created = Date.parse(job.createdAt);
    if (Number.isNaN(created)) return false;
    const age = now - created;
    return age >= 0 && age < RECENT_EXPORT_WINDOW_MS;
  });
}

export function ExportSection() {
  const { t, locale } = useLocaleOptional();
  const queryClient = useQueryClient();
  const [exportId, setExportId] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const query = usePollExport(exportId);
  const myExports = useMyExports();

  const start = async () => {
    setErrMsg(null);
    try {
      const job = await createExport();
      setExportId(job.exportId);
    } catch (e) {
      setErrMsg(errKey(e, 'export'));
    }
  };

  // When the in-session export completes, refresh the recent-exports list so
  // the "recent export" card reflects it the next time the idle state renders.
  useEffect(() => {
    if (query.data?.status === 'completed') {
      void queryClient.invalidateQueries({ queryKey: MY_EXPORTS_QUERY_KEY });
    }
  }, [query.data?.status, queryClient]);

  if (exportId === null) {
    const recent = findRecentExport(myExports.data);
    return (
      <div className="flex flex-col gap-2">
        {recent && <RecentExportCard job={recent} locale={locale} />}
        <Button variant="neutral" className="w-fit" onClick={() => void start()}>
          <Download className="h-4 w-4" strokeWidth={1.75} aria-hidden />
          {t('profile.yourData.export')}
        </Button>
        {errMsg && <p className="text-caption text-ink-secondary">{t(errMsg)}</p>}
      </div>
    );
  }

  const job = query.data;
  if (query.isPending || !job) {
    return <ExportingLabel />;
  }
  if (job.status === 'completed') {
    return (
      <div className="flex flex-col gap-2">
        <Button asChild variant="neutral" className="w-fit">
          <a href={job.downloadUrl} target="_blank" rel="noreferrer">
            <Download className="h-4 w-4" strokeWidth={1.75} aria-hidden />
            {t('profile.yourData.download')}
          </a>
        </Button>
        <p className="text-caption text-ink-secondary">{t('profile.yourData.emailed')}</p>
      </div>
    );
  }
  if (job.status === 'failed') {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-caption text-ink-secondary">{t('profile.yourData.exportFailed')}</p>
        <Button variant="neutral" className="w-fit" onClick={() => setExportId(null)}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }
  return <ExportingLabel />;
}

function RecentExportCard({ job, locale }: { job: ExportJob; locale: Locale }) {
  const { t } = useLocaleOptional();
  const time = relativeTime(job.createdAt ?? '', Date.now(), locale);
  return (
    <div className="flex flex-col gap-2 rounded-lg bg-surface p-4">
      <p className="text-subhead text-ink">{t('profile.yourData.recentReady', { time })}</p>
      <Button asChild variant="neutral" className="w-fit">
        <a href={job.downloadUrl} target="_blank" rel="noreferrer">
          <Download className="h-4 w-4" strokeWidth={1.75} aria-hidden />
          {t('profile.yourData.download')}
        </a>
      </Button>
      <p className="text-caption text-ink-secondary">{t('profile.yourData.recentHint')}</p>
    </div>
  );
}

function ExportingLabel() {
  const { t } = useLocaleOptional();
  return (
    <div className="flex flex-col gap-2" role="status">
      <p className="flex items-center gap-2 text-caption text-ink-secondary">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        {t('profile.yourData.exporting')}
      </p>
      <p className="text-caption text-ink-tertiary">{t('profile.yourData.exportQueued')}</p>
    </div>
  );
}

/* --- import --------------------------------------------------------------- */

const NON_TERMINAL_IMPORT = new Set(['awaiting_upload', 'queued', 'processing']);
const TERMINAL_IMPORT = new Set(['completed', 'failed', 'cancelled']);

/** The most recently created non-terminal import, if any (list is newest-first). */
function findPendingImport(jobs: ImportJob[] | undefined): ImportJob | undefined {
  return jobs?.find((job) => NON_TERMINAL_IMPORT.has(job.status));
}

type ImportWizardStep = 'source' | 'upload' | 'processing' | 'done';

/**
 * The six origins a user can claim, with the exact thing they should upload,
 * the per-source export instructions (rendered as ≤3 bullets), and the
 * matching landing-page guide (empty for ScreenAtlas / other-CSV).
 */
interface SourceOption {
  value: Exclude<ImportSource, 'auto'>;
  nameKey: string;
  uploadKey: string;
  helpKey: string;
}

const SOURCE_OPTIONS: SourceOption[] = [
  {
    value: 'tvtime',
    nameKey: 'profile.import.source.tvtime.name',
    uploadKey: 'profile.import.source.tvtime.upload',
    helpKey: 'profile.import.help.tvtime',
  },
  {
    value: 'trakt',
    nameKey: 'profile.import.source.trakt.name',
    uploadKey: 'profile.import.source.trakt.upload',
    helpKey: 'profile.import.help.trakt',
  },
  {
    value: 'letterboxd',
    nameKey: 'profile.import.source.letterboxd.name',
    uploadKey: 'profile.import.source.letterboxd.upload',
    helpKey: 'profile.import.help.letterboxd',
  },
  {
    value: 'simkl',
    nameKey: 'profile.import.source.simkl.name',
    uploadKey: 'profile.import.source.simkl.upload',
    helpKey: 'profile.import.help.simkl',
  },
  {
    value: 'screenatlas',
    nameKey: 'profile.import.source.screenatlas.name',
    uploadKey: 'profile.import.source.screenatlas.upload',
    helpKey: 'profile.import.help.screenatlas',
  },
  {
    value: 'generic',
    nameKey: 'profile.import.source.generic.name',
    uploadKey: 'profile.import.source.generic.upload',
    helpKey: 'profile.import.help.generic',
  },
];

/** Landing-page guide paths (root of the marketing site), per locale. */
const SOURCE_LANDING_PATHS: Record<string, { en: string; pt: string }> = {
  tvtime: { en: '/tv-time-alternative/', pt: '/br/alternativa-ao-tv-time/' },
  trakt: { en: '/trakt-alternative/', pt: '/br/alternativa-ao-trakt/' },
  letterboxd: { en: '/letterboxd-alternative/', pt: '/br/alternativa-ao-letterboxd/' },
  simkl: { en: '/simkl-alternative/', pt: '/br/alternativa-ao-simkl/' },
};

function landingPathFor(source: ImportSource, locale: Locale): string | undefined {
  return SOURCE_LANDING_PATHS[source]?.[locale === 'pt-BR' ? 'pt' : 'en'];
}

/**
 * Every `source_format` the backend can report → a friendly label. These are
 * brand names + universal tech terms, so they live here rather than the
 * locale catalogs.
 */
const SOURCE_FORMAT_LABELS: Record<string, string> = {
  screenatlas_json: 'ScreenAtlas export',
  generic_csv: 'CSV',
  tvtime_zip: 'TV Time export',
  tvtime_csv: 'TV Time CSV',
  trakt_zip: 'Trakt export',
  trakt_json: 'Trakt JSON',
  letterboxd_zip: 'Letterboxd export',
  letterboxd_csv: 'Letterboxd CSV',
  simkl_zip: 'Simkl export',
  simkl_json: 'Simkl JSON',
  simkl_csv: 'Simkl CSV',
  tmdb_tracker_json: 'Tracker JSON',
};

function sourceFormatLabel(format: string | undefined): string {
  if (!format) return '';
  return SOURCE_FORMAT_LABELS[format] ?? format;
}

/** Map a failed job's `error` code to a descriptive copy key. */
function importErrorKey(error: string | undefined): string {
  const code = (error ?? '').split(':')[0].trim();
  switch (code) {
    case 'unsupported_format':
      return 'profile.import.failUnsupported';
    case 'archive_invalid':
      return 'profile.import.failArchive';
    case 'import_too_large':
      return 'profile.import.failTooLarge';
    case 'invalid_source_key':
      return 'profile.import.failSourceKey';
    default:
      return 'profile.yourData.importFailed';
  }
}

/** The accepted file types for the upload input. */
const IMPORT_ACCEPT =
  '.zip,.json,.csv,.txt,application/zip,application/x-zip-compressed,application/json,text/csv,text/plain';

export function ImportWizard({ onGoToLibrary }: { onGoToLibrary?: () => void } = {}) {
  const { locale } = useLocaleOptional();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<ImportWizardStep>('source');
  const [source, setSource] = useState<ImportSource>('auto');
  const [importId, setImportId] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const query = usePollImport(importId);
  const myImports = useMyImports();
  const inspected = useRef(false);

  const existing = findPendingImport(myImports.data);

  // On mount, resume an in-flight import: a queued/processing job jumps
  // straight to the processing step (and polls it); an `awaiting_upload` job
  // is surfaced in the upload step's resume banner instead.
  useEffect(() => {
    if (inspected.current || !myImports.data) return;
    inspected.current = true;
    const job = findPendingImport(myImports.data);
    if (!job) return;
    if (job.status === 'awaiting_upload') {
      setStep('upload');
    } else {
      setImportId(job.importId);
      setStep('processing');
    }
  }, [myImports.data]);

  // Once the polled job reaches a terminal status, present the "Done" step.
  useEffect(() => {
    if (step === 'processing' && query.data && TERMINAL_IMPORT.has(query.data.status)) {
      setStep('done');
    }
  }, [query.data, step]);

  const choose = (value: ImportSource) => {
    setSource(value);
    setErrMsg(null);
    setStep('upload');
  };

  const onFile = async (file: File) => {
    setErrMsg(null);
    setUploadProgress(0);
    setStep('processing');
    try {
      const id = await importUpload(file, {
        source,
        onProgress: (fraction) => setUploadProgress(fraction),
      });
      setImportId(id);
    } catch (e) {
      setErrMsg(errKey(e, 'import'));
      setStep('upload');
    } finally {
      setUploadProgress(null);
    }
  };

  const resume = async (id: string) => {
    setErrMsg(null);
    try {
      await startImport(id);
      setImportId(id);
      setStep('processing');
    } catch (e) {
      setErrMsg(errKey(e, 'import'));
    }
  };

  const cancel = async (id: string) => {
    try {
      await cancelImport(id);
    } finally {
      setImportId(null);
      setErrMsg(null);
      setStep('source');
      void queryClient.invalidateQueries({ queryKey: MY_IMPORTS_QUERY_KEY });
    }
  };

  const reset = () => {
    setImportId(null);
    setErrMsg(null);
    setStep('source');
  };

  const backToSource = () => {
    setErrMsg(null);
    setStep('source');
  };

  return (
    <div className="flex flex-col gap-3">
      {step === 'source' && <SourceStep onChoose={choose} />}

      {step === 'upload' && (
        <UploadStep
          source={source}
          locale={locale}
          uploading={uploadProgress !== null}
          fraction={uploadProgress ?? 0}
          errMsg={errMsg}
          awaiting={existing?.status === 'awaiting_upload' ? existing : undefined}
          fileRef={fileRef}
          onBack={backToSource}
          onFile={onFile}
          onResume={() => void resume(existing!.importId)}
          onCancel={() => void cancel(existing!.importId)}
        />
      )}

      {step === 'processing' && (
        <ProcessingStep
          uploading={uploadProgress !== null}
          fraction={uploadProgress ?? 0}
          pending={query.isPending}
          job={query.data}
          errMsg={errMsg}
          onResume={() => void resume(importId as string)}
          onCancel={() => void cancel(importId as string)}
        />
      )}

      {step === 'done' && (
        <DoneStep job={query.data} onReset={reset} onGoToLibrary={onGoToLibrary} />
      )}
    </div>
  );
}

/* --- step: source --------------------------------------------------------- */

function SourceStep({ onChoose }: { onChoose: (source: ImportSource) => void }) {
  const { t } = useLocaleOptional();
  return (
    <div className="flex flex-col gap-2">
      <p className="text-subhead text-ink">{t('profile.import.source.title')}</p>
      <div className="flex flex-col gap-2">
        {SOURCE_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChoose(option.value)}
            aria-label={t(option.nameKey)}
            className="flex flex-col items-start gap-0.5 rounded-lg bg-surface p-4 text-left hover:bg-surface-alt"
          >
            <span className="text-subhead font-medium text-ink">{t(option.nameKey)}</span>
            <span className="text-caption text-ink-secondary">{t(option.uploadKey)}</span>
          </button>
        ))}
      </div>
      <Button
        variant="ghost"
        className="w-fit text-caption"
        onClick={() => onChoose('auto')}
      >
        {t('profile.import.source.notSure')}
      </Button>
    </div>
  );
}

/* --- step: upload --------------------------------------------------------- */

function UploadStep({
  source,
  locale,
  uploading,
  fraction,
  errMsg,
  awaiting,
  fileRef,
  onBack,
  onFile,
  onResume,
  onCancel,
}: {
  source: ImportSource;
  locale: Locale;
  uploading: boolean;
  fraction: number;
  errMsg: string | null;
  awaiting: ImportJob | undefined;
  fileRef: RefObject<HTMLInputElement | null>;
  onBack: () => void;
  onFile: (file: File) => void;
  onResume: () => void;
  onCancel: () => void;
}) {
  const { t } = useLocaleOptional();
  const option = SOURCE_OPTIONS.find((o) => o.value === source);
  const name = option ? t(option.nameKey) : '';
  const landingPath = landingPathFor(source, locale);

  if (awaiting) {
    return (
      <div className="flex flex-col gap-2">
        <BackLink onBack={onBack} />
        <ImportWaiting
          errMsg={errMsg}
          onResume={onResume}
          onCancel={onCancel}
        />
      </div>
    );
  }

  const helpLines = option ? t(option.helpKey).split('\n') : [];

  return (
    <div className="flex flex-col gap-2">
      <BackLink onBack={onBack} />
      <p className="text-subhead text-ink">{name}</p>
      {helpLines.length > 0 && (
        <ul className="flex flex-col gap-1">
          {helpLines.map((line) => (
            <li key={line} className="text-caption text-ink-secondary">
              {line}
            </li>
          ))}
        </ul>
      )}
      {landingPath && (
        <a
          href={landingPath}
          target="_blank"
          rel="noreferrer"
          className="text-caption text-ink-secondary underline-offset-2 hover:text-ink hover:underline"
        >
          {t('profile.import.upload.guide', { source: name })}
        </a>
      )}

      <p className="text-caption text-ink-tertiary">{t('profile.import.upload.hint')}</p>

      {uploading ? (
        <UploadingProgress fraction={fraction} />
      ) : (
        <Button variant="accent" className="w-fit" onClick={() => fileRef.current?.click()}>
          <Upload className="h-4 w-4" strokeWidth={1.75} aria-hidden />
          {t('profile.import.upload.choose')}
        </Button>
      )}

      <input
        ref={fileRef}
        type="file"
        accept={IMPORT_ACCEPT}
        className="sr-only"
        aria-label={t('profile.import.upload.choose')}
        disabled={uploading}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onFile(file);
          event.target.value = '';
        }}
      />
      {errMsg && <p className="text-caption text-ink-secondary">{t(errMsg)}</p>}
    </div>
  );
}

function BackLink({ onBack }: { onBack: () => void }) {
  const { t } = useLocaleOptional();
  return (
    <button
      type="button"
      onClick={onBack}
      className="-ml-1 flex w-fit items-center gap-0.5 rounded px-1 py-0.5 text-caption text-ink-tertiary hover:text-ink"
    >
      <ChevronLeft className="h-4 w-4" strokeWidth={1.75} aria-hidden />
      {t('common.back')}
    </button>
  );
}

function ImportWaiting({
  errMsg,
  onResume,
  onCancel,
}: {
  errMsg: string | null;
  onResume: () => void;
  onCancel: () => void;
}) {
  const { t } = useLocaleOptional();
  return (
    <div className="flex flex-col gap-2">
      <p className="text-caption text-ink-secondary">{t('profile.yourData.importPending')}</p>
      <div className="flex gap-2">
        <Button variant="neutral" className="w-fit" onClick={onResume}>
          {t('profile.yourData.resume')}
        </Button>
        <Button variant="neutral" className="w-fit" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
      {errMsg && <p className="text-caption text-ink-secondary">{t(errMsg)}</p>}
    </div>
  );
}

/* --- step: processing ----------------------------------------------------- */

function ProcessingStep({
  uploading,
  fraction,
  pending,
  job,
  errMsg,
  onResume,
  onCancel,
}: {
  uploading: boolean;
  fraction: number;
  pending: boolean;
  job: ImportJob | undefined;
  errMsg: string | null;
  onResume: () => void;
  onCancel: () => void;
}) {
  if (uploading) {
    return <UploadingProgress fraction={fraction} />;
  }

  if (pending || !job) {
    return <ImportingLabel />;
  }

  if (job.status === 'awaiting_upload') {
    return <ImportWaiting errMsg={errMsg} onResume={onResume} onCancel={onCancel} />;
  }

  if (job.status === 'queued') {
    return <DetectingLabel />;
  }

  return <ImportingLabel />;
}

function DetectingLabel() {
  const { t } = useLocaleOptional();
  return (
    <p className="flex items-center gap-2 text-caption text-ink-secondary" role="status">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {t('profile.yourData.detecting')}
    </p>
  );
}

function ImportingLabel() {
  const { t } = useLocaleOptional();
  return (
    <p className="flex items-center gap-2 text-caption text-ink-secondary" role="status">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {t('profile.yourData.importing')}
    </p>
  );
}

function UploadingProgress({ fraction }: { fraction: number }) {
  const { t } = useLocaleOptional();
  const percent = Math.max(0, Math.min(100, Math.round(fraction * 100)));
  return (
    <div className="flex flex-col gap-2" role="status">
      <p className="flex items-center gap-2 text-caption text-ink-secondary">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        {t('profile.yourData.uploading', { percent })}
      </p>
      <div
        className="h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-surface-alt"
        aria-hidden
      >
        <div className="h-full rounded-full bg-accent" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

/* --- step: done ----------------------------------------------------------- */

function DoneStep({
  job,
  onReset,
  onGoToLibrary,
}: {
  job: ImportJob | undefined;
  onReset: () => void;
  onGoToLibrary?: () => void;
}) {
  const { t } = useLocaleOptional();
  const navigate = useNavigate();

  if (!job) {
    return <ImportingLabel />;
  }

  if (job.status === 'completed') {
    if (job.duplicateOf) {
      return (
        <div className="flex flex-col gap-3" role="status">
          <p className="text-caption text-ink-secondary">{t('profile.yourData.duplicate')}</p>
          <DoneActions onReset={onReset} onGoToLibrary={onGoToLibrary} navigate={navigate} />
        </div>
      );
    }
    return (
      <ImportDone
        job={job}
        onReset={onReset}
        onGoToLibrary={onGoToLibrary}
        navigate={navigate}
      />
    );
  }

  if (job.status === 'failed' || job.status === 'cancelled') {
    const key = job.status === 'failed' ? importErrorKey(job.error) : 'profile.yourData.importFailed';
    return (
      <div className="flex flex-col gap-2">
        <p className="text-caption text-ink-secondary" role="status">
          {t(key)}
        </p>
        <Button variant="neutral" className="w-fit" onClick={onReset}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  return <ImportingLabel />;
}

function ImportDone({
  job,
  onReset,
  onGoToLibrary,
  navigate,
}: {
  job: ImportJob;
  onReset: () => void;
  onGoToLibrary?: () => void;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const { t } = useLocaleOptional();
  const counts = job.counts ?? {};
  const format = job.detected?.format ?? job.sourceFormat;
  const label = sourceFormatLabel(format);
  const report = useImportReport(job.reportUrl);

  const unmatchedCount =
    report.data?.unmatched.length
      ? report.data.unmatched.reduce((n, e) => n + e.count, 0)
      : (counts.shows_unmatched ?? 0) + (counts.movies_unmatched ?? 0);

  return (
    <div className="flex flex-col gap-3" role="status">
      {label && (
        <p className="text-caption text-ink-secondary">
          {t('profile.import.detected', { format: label })}
        </p>
      )}

      <ul className="flex flex-wrap gap-x-4 gap-y-1">
        <CountItem label={t('profile.import.count.shows')} value={counts.shows_matched ?? 0} />
        <CountItem label={t('profile.import.count.movies')} value={counts.movies_matched ?? 0} />
        <CountItem label={t('profile.import.count.episodes')} value={counts.episodes_marked ?? 0} />
        <CountItem label={t('profile.import.count.ratings')} value={counts.ratings_applied ?? 0} />
        <CountItem
          label={t('profile.import.count.watchlist')}
          value={counts.watchlist_applied ?? 0}
        />
        <CountItem
          label={t('profile.import.count.rewatches')}
          value={counts.rewatches_applied ?? 0}
        />
      </ul>

      {job.detected?.hintMismatch && (
        <p className="text-caption text-ink-secondary">
          {t('profile.import.hintMismatch', { format: label })}
        </p>
      )}

      {job.reportUrl && (
        <div className="flex flex-col gap-2">
          {report.data && report.data.unmatched.length > 0 && (
            <div className="flex flex-col gap-1">
              <p className="text-caption text-ink-secondary">
                {t('profile.import.report.title')}
              </p>
              <ul className="flex flex-col gap-1">
                {report.data.unmatched.slice(0, 5).map((entry) => (
                  <li key={entry.title} className="flex flex-col">
                    <span className="text-subhead text-ink">{entry.title}</span>
                    <span className="text-caption text-ink-tertiary">
                      {t(`profile.import.report.${entry.mediaType}`)} ·{' '}
                      {t('profile.import.report.rows', { count: entry.count })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Button asChild variant="neutral" className="w-fit">
            <a href={job.reportUrl} target="_blank" rel="noreferrer">
              <Download className="h-4 w-4" strokeWidth={1.75} aria-hidden />
              {unmatchedCount > 0
                ? t('profile.yourData.unmatched', { count: unmatchedCount })
                : t('profile.import.report.download')}
            </a>
          </Button>
        </div>
      )}

      <DoneActions onReset={onReset} onGoToLibrary={onGoToLibrary} navigate={navigate} />
    </div>
  );
}

function CountItem({ label, value }: { label: string; value: number }) {
  return (
    <li className="flex items-baseline gap-1.5">
      <span className="text-headline font-semibold text-ink">{value}</span>
      <span className="text-caption text-ink-secondary">{label}</span>
    </li>
  );
}

function DoneActions({
  onReset,
  onGoToLibrary,
  navigate,
}: {
  onReset: () => void;
  onGoToLibrary?: () => void;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const { t } = useLocaleOptional();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant="accent"
        onClick={() => {
          onGoToLibrary?.();
          navigate(NAV_ITEMS[0].to);
        }}
      >
        <LibraryBig className="h-4 w-4" strokeWidth={1.75} aria-hidden />
        {t('profile.yourData.goToLibrary')}
      </Button>
      <Button variant="ghost" className="w-fit" onClick={onReset}>
        {t('profile.import.again')}
      </Button>
    </div>
  );
}
